import type { MutationFunction, UseMutationOptions, UseMutationResult } from '@tanstack/react-query';
import { useMutation } from '@tanstack/react-query';
import type { BodyType, ErrorType } from '../axios';
import { request } from '../axios';
import type { SecondaryDiagnosisDto, SymptomDto } from '../generated/model';

type RequestOptions = Parameters<typeof request>[1];

interface SymptomsNoteDto {
  note: string;
}

interface NoteDto {
  note: string;
}

export const updateAnamnesisSymptomsNote = (
  id: string,
  symptomsNoteDto: BodyType<SymptomsNoteDto>,
  options?: RequestOptions,
) => {
  return request<void>(
    {
      url: `/api/anamneses/${id}/symptoms-note`,
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      data: symptomsNoteDto,
    },
    options,
  );
};

export const getUpdateAnamnesisSymptomsNoteMutationOptions = <
  TData = Awaited<ReturnType<typeof updateAnamnesisSymptomsNote>>,
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<TData, TError, { id: string; data: BodyType<SymptomsNoteDto> }, TContext>;
  request?: RequestOptions;
}) => {
  const mutationKey = ['updateAnamnesisSymptomsNote'];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateAnamnesisSymptomsNote>>,
    { id: string; data: BodyType<SymptomsNoteDto> }
  > = (props) => {
    const { id, data } = props ?? {};

    return updateAnamnesisSymptomsNote(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions } as UseMutationOptions<
    TData,
    TError,
    { id: string; data: BodyType<SymptomsNoteDto> },
    TContext
  >;
};

export const useUpdateAnamnesisSymptomsNote = <
  TData = Awaited<ReturnType<typeof updateAnamnesisSymptomsNote>>,
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<TData, TError, { id: string; data: BodyType<SymptomsNoteDto> }, TContext>;
  request?: RequestOptions;
}): UseMutationResult<TData, TError, { id: string; data: BodyType<SymptomsNoteDto> }, TContext> => {
  const mutationOptions = getUpdateAnamnesisSymptomsNoteMutationOptions(options);

  return useMutation(mutationOptions);
};

export const updateAnamnesisSymptoms = (id: string, symptomDto: BodyType<SymptomDto[]>, options?: RequestOptions) => {
  return request<void>(
    {
      url: `/api/anamneses/${id}/symptoms`,
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      data: symptomDto,
    },
    options,
  );
};

export const getUpdateAnamnesisSymptomsMutationOptions = <
  TData = Awaited<ReturnType<typeof updateAnamnesisSymptoms>>,
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<TData, TError, { id: string; data: BodyType<SymptomDto[]> }, TContext>;
  request?: RequestOptions;
}) => {
  const mutationKey = ['updateAnamnesisSymptoms'];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateAnamnesisSymptoms>>,
    { id: string; data: BodyType<SymptomDto[]> }
  > = (props) => {
    const { id, data } = props ?? {};

    return updateAnamnesisSymptoms(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions } as UseMutationOptions<
    TData,
    TError,
    { id: string; data: BodyType<SymptomDto[]> },
    TContext
  >;
};

export const useUpdateAnamnesisSymptoms = <
  TData = Awaited<ReturnType<typeof updateAnamnesisSymptoms>>,
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<TData, TError, { id: string; data: BodyType<SymptomDto[]> }, TContext>;
  request?: RequestOptions;
}): UseMutationResult<TData, TError, { id: string; data: BodyType<SymptomDto[]> }, TContext> => {
  const mutationOptions = getUpdateAnamnesisSymptomsMutationOptions(options);

  return useMutation(mutationOptions);
};

export const updateAnamnesisSecondaryDiagnoses = (
  id: string,
  secondaryDiagnosisDto: BodyType<SecondaryDiagnosisDto[]>,
  options?: RequestOptions,
) => {
  return request<void>(
    {
      url: `/api/anamneses/${id}/secondary-diagnoses`,
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      data: secondaryDiagnosisDto,
    },
    options,
  );
};

export const getUpdateAnamnesisSecondaryDiagnosesMutationOptions = <
  TData = Awaited<ReturnType<typeof updateAnamnesisSecondaryDiagnoses>>,
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<TData, TError, { id: string; data: BodyType<SecondaryDiagnosisDto[]> }, TContext>;
  request?: RequestOptions;
}) => {
  const mutationKey = ['updateAnamnesisSecondaryDiagnoses'];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateAnamnesisSecondaryDiagnoses>>,
    { id: string; data: BodyType<SecondaryDiagnosisDto[]> }
  > = (props) => {
    const { id, data } = props ?? {};

    return updateAnamnesisSecondaryDiagnoses(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions } as UseMutationOptions<
    TData,
    TError,
    { id: string; data: BodyType<SecondaryDiagnosisDto[]> },
    TContext
  >;
};

export const useUpdateAnamnesisSecondaryDiagnoses = <
  TData = Awaited<ReturnType<typeof updateAnamnesisSecondaryDiagnoses>>,
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<TData, TError, { id: string; data: BodyType<SecondaryDiagnosisDto[]> }, TContext>;
  request?: RequestOptions;
}): UseMutationResult<TData, TError, { id: string; data: BodyType<SecondaryDiagnosisDto[]> }, TContext> => {
  const mutationOptions = getUpdateAnamnesisSecondaryDiagnosesMutationOptions(options);

  return useMutation(mutationOptions);
};

export const updateAnamnesisSecondaryDiagnosesNote = (
  id: string,
  secondaryDiagnosesNoteDto: BodyType<NoteDto>,
  options?: RequestOptions,
) => {
  return request<void>(
    {
      url: `/api/anamneses/${id}/secondary-diagnoses-note`,
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      data: secondaryDiagnosesNoteDto,
    },
    options,
  );
};

export const getUpdateAnamnesisSecondaryDiagnosesNoteMutationOptions = <
  TData = Awaited<ReturnType<typeof updateAnamnesisSecondaryDiagnosesNote>>,
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<TData, TError, { id: string; data: BodyType<NoteDto> }, TContext>;
  request?: RequestOptions;
}) => {
  const mutationKey = ['updateAnamnesisSecondaryDiagnosesNote'];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateAnamnesisSecondaryDiagnosesNote>>,
    { id: string; data: BodyType<NoteDto> }
  > = (props) => {
    const { id, data } = props ?? {};

    return updateAnamnesisSecondaryDiagnosesNote(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions } as UseMutationOptions<
    TData,
    TError,
    { id: string; data: BodyType<NoteDto> },
    TContext
  >;
};

export const useUpdateAnamnesisSecondaryDiagnosesNote = <
  TData = Awaited<ReturnType<typeof updateAnamnesisSecondaryDiagnosesNote>>,
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<TData, TError, { id: string; data: BodyType<NoteDto> }, TContext>;
  request?: RequestOptions;
}): UseMutationResult<TData, TError, { id: string; data: BodyType<NoteDto> }, TContext> => {
  const mutationOptions = getUpdateAnamnesisSecondaryDiagnosesNoteMutationOptions(options);

  return useMutation(mutationOptions);
};
